import { Settings, LogOut, ExternalLink, ArrowRightLeft } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { decodeMirrorId } from "@/lib/tokens";

const ONBOARDING_URL = "https://makeyourself.ai/docs/excel-onboarding";
const VERSION = import.meta.env.VITE_VERSION ?? "0.1.0";

export function SettingsPanel({
  tenant,
  onSignOut,
  onSwitchWorkspace,
}: {
  tenant: string;
  onSignOut: () => void;
  onSwitchWorkspace: () => void;
}) {
  const mirrorId = decodeMirrorId();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          aria-label="Settings"
          className="rounded-md p-1.5 text-[var(--text-muted)] hover:bg-[var(--hover-overlay)] hover:text-[var(--text-primary)]"
        >
          <Settings size={16} strokeWidth={1.5} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="bg-[var(--surface-base)] text-[var(--text-primary)]"
      >
        <SheetHeader>
          <SheetTitle className="text-[var(--text-primary)]">Settings</SheetTitle>
          <SheetDescription className="text-[var(--text-secondary)]">
            Account and app info.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-2 flex flex-col gap-4 px-4 pb-4 text-[13px]">
          <Field label="Mirror ID" value={mirrorId ?? "—"} mono />
          <Field label="Tenant" value={tenant} />
          <Field label="Version" value={VERSION} mono />

          <a
            href={ONBOARDING_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-[var(--brand-accent)] hover:underline"
          >
            Onboarding guide
            <ExternalLink size={12} strokeWidth={1.5} />
          </a>

          <button
            type="button"
            onClick={onSwitchWorkspace}
            className="mt-2 flex items-center justify-center gap-2 rounded-md border border-[var(--border-default)] px-3 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-overlay)]"
          >
            <ArrowRightLeft size={14} strokeWidth={1.5} />
            Switch workspace
          </button>

          <button
            type="button"
            onClick={onSignOut}
            className="flex items-center justify-center gap-2 rounded-md border border-[var(--border-default)] px-3 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-overlay)]"
          >
            <LogOut size={14} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </span>
      <span
        className={
          mono
            ? "font-mono text-[12px] text-[var(--text-primary)] break-all"
            : "text-[var(--text-primary)]"
        }
      >
        {value}
      </span>
    </div>
  );
}
